import React from 'react';

export interface ProgressBarProps {
  value: number;
  max?: number;
  variant?: 'default' | 'success' | 'warning' | 'info' | 'brand';
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  showPercentage?: boolean;
  label?: string;
  className?: string;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max = 100,
  variant = 'default',
  size = 'md',
  showLabel = false,
  showPercentage = false,
  label,
  className = '',
}) => {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);

  const getVariantClasses = (variant: string): string => {
    switch (variant) {
      case 'success':
        return 'progress-fill-success';
      case 'warning':
        return 'progress-fill-warning';
      case 'info':
        return 'progress-fill-info';
      case 'brand':
        return 'progress-fill-brand';
      default:
        return 'progress-fill-info';
    }
  };

  const getSizeClasses = (size: string): string => {
    switch (size) {
      case 'sm':
        return 'h-1.5';
      case 'lg':
        return 'h-3';
      case 'md':
      default:
        return 'h-2';
    }
  };

  const getAutoVariant = (percentage: number): string => {
    if (percentage >= 90) return 'success';
    if (percentage >= 70) return 'info';
    if (percentage >= 40) return 'warning';
    return 'info';
  };

  const finalVariant = variant === 'default' ? getAutoVariant(percentage) : variant;

  return (
    <div className={className}>
      {(showLabel || label) && (
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-gray-600 font-medium">{label || 'Progress'}</span>
          {showPercentage && (
            <span className="text-gray-900 font-medium">{Math.round(percentage)}%</span>
          )}
        </div>
      )}
      <div className={`progress-bar ${getSizeClasses(size)}`}>
        <div
          className={`progress-fill ${getVariantClasses(finalVariant)}`}
          style={{ width: `${percentage}%` }}
          role="progressbar"
          aria-valuenow={value}
          aria-valuemin={0}
          aria-valuemax={max}
          aria-label={label || `Progress: ${Math.round(percentage)}%`}
        />
      </div>
    </div>
  );
};

export default ProgressBar;
